import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, Loader2 } from 'lucide-react';
import axios from 'axios';
import { trackPurchase } from '../lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PaymentConfirmation = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const purchaseTrackedRef = useRef(false);

  useEffect(() => {
    const fetchOrderStatus = async () => {
      try {
        const response = await axios.get(`${API}/orders/${orderId}`);
        const data = response.data;
        setOrder(data);
        setLoading(false);

        // Fire purchase event once when paid
        if (data.status === 'paid' && !purchaseTrackedRef.current) {
          purchaseTrackedRef.current = true;
          trackPurchase({
            orderId: data.order_id || orderId,
            value: data.total || 0,
            quantity: data.quantity || 1,
            source: data.source || 'direct',
          });
        }

        // If still pending, poll every 3 seconds
        if (data.status === 'pending_payment' || data.status === 'processing') {
          setTimeout(fetchOrderStatus, 3000);
        }
      } catch (err) {
        setError('Error al verificar el estado del pago');
        setLoading(false);
      }
    };

    if (orderId) {
      fetchOrderStatus();
    }
  }, [orderId]);

  if (loading && !order) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="animate-spin text-blue-900 mx-auto mb-4" size={64} />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Verificando tu pago...</h2>
          <p className="text-gray-600">Por favor espera un momento</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 lg:p-12 max-w-2xl w-full text-center">
          <XCircle className="text-red-500 mx-auto mb-6" size={80} />
          <h2 className="text-3xl font-black text-gray-900 mb-4">Error</h2>
          <p className="text-xl text-gray-700 mb-8">{error}</p>
          <a
            href="/"
            className="inline-block bg-blue-900 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-800 transition-all hover:scale-105"
          >
            Volver al inicio
          </a>
        </div>
      </div>
    );
  }

  // Payment successful
  if (order?.status === 'paid') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 lg:p-12 max-w-2xl w-full">
          <div className="text-center">
            <CheckCircle className="text-green-500 mx-auto mb-6" size={80} />
            <h2 className="text-3xl lg:text-4xl font-black text-gray-900 mb-4">
              ¡Pago Exitoso!
            </h2>
            <p className="text-xl text-gray-700 mb-8">
              Tu pedido ha sido confirmado. Te enviaremos los detalles a tu correo.
            </p>

            <div className="bg-gray-50 rounded-2xl p-6 mb-8 text-left">
              <h3 className="font-bold text-lg text-gray-900 mb-4">Resumen de tu pedido</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Número de orden:</span>
                  <span className="font-semibold text-gray-900">{order.order_id.substring(0, 8)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total pagado:</span>
                  <span className="font-bold text-blue-900 text-xl">${order.total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Estado:</span>
                  <span className="inline-block bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                    Pagado
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6 mb-8">
              <h3 className="font-bold text-lg text-blue-900 mb-2">¿Qué sigue?</h3>
              <p className="text-gray-700">
                1. Recibirás un correo de confirmación con los detalles de tu pedido<br />
                2. Te contactaremos para coordinar la entrega<br />
                3. ¡Disfruta tus nuevas Gafas La Tri!
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/"
                className="bg-blue-900 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-800 transition-all hover:scale-105"
              >
                Volver al inicio
              </a>
              <a
                href="https://www.instagram.com/gafasdelatri"
                target="_blank"
                rel="noopener noreferrer"
                className="border-2 border-blue-900 text-blue-900 px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-50 transition-all"
              >
                Síguenos en Instagram
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Payment failed
  if (order?.status === 'failed') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 lg:p-12 max-w-2xl w-full text-center">
          <XCircle className="text-red-500 mx-auto mb-6" size={80} />
          <h2 className="text-3xl font-black text-gray-900 mb-4">Pago no completado</h2>
          <p className="text-xl text-gray-700 mb-8">
            Tu pago no pudo ser procesado. Por favor intenta nuevamente o contáctanos por WhatsApp.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/#comprar"
              className="bg-blue-900 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-800 transition-all hover:scale-105"
            >
              Intentar nuevamente
            </a>
            <a
              href="https://wa.me/593962888130"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-500 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-green-600 transition-all hover:scale-105"
            >
              Contactar por WhatsApp
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Payment pending/processing
  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-white flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl p-8 lg:p-12 max-w-2xl w-full text-center">
        <Clock className="text-yellow-500 mx-auto mb-6 animate-pulse" size={80} />
        <h2 className="text-3xl font-black text-gray-900 mb-4">Procesando tu pago...</h2>
        <p className="text-xl text-gray-700 mb-8">
          Estamos verificando tu pago con Payphone. Esta página se actualizará automáticamente.
        </p>
        <div className="bg-gray-50 rounded-2xl p-6">
          <p className="text-gray-600">
            Número de orden: <span className="font-semibold">{order?.order_id.substring(0, 8)}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentConfirmation;
